import React, { useCallback, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import {
  useDownloadSubscriptionDocument,
  useSubscriptionDocuments,
} from '../../../../api/subscriptions';
import { SubscriptionDocument } from '../../../../types/subscriptions';
import { DetailSection } from '../../../../components/ui/detailView';
import { StatusChip } from '../../../../components/ui/statusChip/statusChip.component';
import { formatDate, formatNumber } from '../../../../utils';
import { ERROR_SNACKBAR } from '../../../../consts';

type DocumentStatus = 'paid' | 'awaiting' | 'cancelled';

const STATUS_COLOR_MAP: Record<DocumentStatus, 'success' | 'warning' | 'default'> = {
  paid: 'success',
  awaiting: 'warning',
  cancelled: 'default',
};

/** An invoice is only ever issued once paid; a proforma is settled once its payment completes. */
const documentStatus = (doc: SubscriptionDocument): DocumentStatus => {
  if (doc.is_paid || doc.payment_status === 'CO') {
    return 'paid';
  }
  return doc.payment_status === 'CA' ? 'cancelled' : 'awaiting';
};

/** Proformas and invoices for the subscription, each downloadable as a PDF. */
export const SubscriptionDocuments: React.FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { query, loading } = useSubscriptionDocuments();
  const { download } = useDownloadSubscriptionDocument();
  const [documents, setDocuments] = useState<SubscriptionDocument[] | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setDocuments(await query());
  }, [query]);

  // A confirmed payment or a new charge adds a document; both refresh the tenant.
  useEffect(() => {
    load();
    window.addEventListener('tenantUpdated', load);
    return () => window.removeEventListener('tenantUpdated', load);
  }, [load]);

  const handleDownload = async (doc: SubscriptionDocument) => {
    setDownloadingId(doc.id);
    const { success } = await download(doc.id, doc.number);
    setDownloadingId(null);
    if (!success) {
      enqueueSnackbar(t('Could not download the document. Please try again.'), ERROR_SNACKBAR);
    }
  };

  const kindLabel: Record<SubscriptionDocument['kind'], string> = {
    PRO: t('Proforma'),
    VAT: t('Invoice'),
  };
  const paymentKindLabel: Record<SubscriptionDocument['payment_kind'], string> = {
    SUBSCRIPTION: t('Subscription fee'),
    PRORATION: t('Plan upgrade surcharge'),
    FINAL: t('Final settlement'),
  };
  const statusLabel: Record<DocumentStatus, string> = {
    paid: t('Paid'),
    awaiting: t('Awaiting payment'),
    cancelled: t('Cancelled'),
  };

  let content: React.ReactNode;
  if (documents === null) {
    content = loading ? (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    ) : (
      <Typography variant="body2" color="error.main" sx={{ p: 2 }}>
        {t('Could not load the documents.')}
      </Typography>
    );
  } else if (documents.length === 0) {
    content = (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        {t('No documents yet.')}
      </Typography>
    );
  } else {
    content = (
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('Number')}</TableCell>
              <TableCell>{t('Document')}</TableCell>
              <TableCell>{t('For')}</TableCell>
              <TableCell>{t('Date of issue')}</TableCell>
              <TableCell align="right">{t('Amount')}</TableCell>
              <TableCell>{t('Status')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {documents.map((doc) => {
              const status = documentStatus(doc);
              return (
                <TableRow key={doc.id}>
                  <TableCell>{doc.number}</TableCell>
                  <TableCell>{kindLabel[doc.kind]}</TableCell>
                  <TableCell>
                    {paymentKindLabel[doc.payment_kind]}
                    {doc.billing_period_start &&
                      ` — ${dayjs(doc.billing_period_start).format('MM.YYYY')}`}
                  </TableCell>
                  <TableCell>{formatDate(doc.issued_at)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {`${formatNumber(doc.total)} ${doc.currency}`}
                  </TableCell>
                  <TableCell>
                    <StatusChip
                      label={statusLabel[status]}
                      value={status}
                      colorMap={STATUS_COLOR_MAP}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('Download PDF')}>
                      <span>
                        <IconButton
                          size="small"
                          aria-label={t('Download PDF')}
                          disabled={downloadingId === doc.id}
                          onClick={() => handleDownload(doc)}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    );
  }

  return <DetailSection title={t('Documents')}>{content}</DetailSection>;
};
